import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Sparkles } from 'lucide-react';

/**
 * Tutoriel interactif au premier lancement
 * Affiche des bulles d'aide au-dessus des éléments clés
 */
const InteractiveTutorial = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState(false);

  const steps = [
    {
      title: '👋 Bienvenue sur Ovrio',
      description: 'Découvrez comment créer votre premier devis en quelques clics. Ce tutoriel dure environ 1 minute.',
      target: null, // Modal plein écran
      position: 'center'
    },
    {
      title: '📊 Votre Tableau de Bord',
      description: 'Ici vous voyez votre chiffre d\'affaires, vos devis en attente et vos factures impayées.',
      target: 'dashboard',
      position: 'bottom'
    },
    {
      title: '📄 Créer un Devis',
      description: 'Cliquez sur "Devis & Factures" dans le menu pour accéder à vos documents.',
      target: 'nav-gestion',
      position: 'right'
    },
    {
      title: '👥 Gérer vos Clients',
      description: 'La section Clients vous permet d\'ajouter et gérer vos contacts facilement.',
      target: 'nav-clients',
      position: 'right'
    },
    {
      title: '🏗️ Suivre vos Chantiers',
      description: 'Avec l\'offre Business, vous pouvez suivre vos chantiers en temps réel avec photos et tâches.',
      target: 'nav-chantiers',
      position: 'right'
    },
    {
      title: '✅ Prêt à commencer !',
      description: 'Vous êtes maintenant prêt à utiliser Ovrio. Vous pouvez relancer ce tutoriel depuis les Paramètres.',
      target: null,
      position: 'center'
    }
  ];

  const currentStep = steps[step];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = () => {
    setCompleted(true);
    setTimeout(() => {
      onComplete();
    }, 500);
  };

  const handleSkipTutorial = () => {
    onSkip();
  };

  // Modal centré pour les étapes sans target
  if (!currentStep.target) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] animate-in fade-in">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8 animate-in zoom-in-95">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Sparkles className="text-white" size={24} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-800">{currentStep.title}</h2>
              <p className="text-xs text-slate-400">Étape {step + 1} / {steps.length}</p>
            </div>
            <button
              onClick={handleSkipTutorial}
              className="text-slate-400 hover:text-slate-600 transition"
            >
              <X size={24} />
            </button>
          </div>

          <p className="text-slate-600 mb-8 leading-relaxed">{currentStep.description}</p>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
                style={{ width: `${((step + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex gap-3">
            {step > 0 && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition flex items-center gap-2"
              >
                <ChevronLeft size={16} />
                Précédent
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-bold hover:scale-105 transition transform flex items-center justify-center gap-2"
            >
              {step === steps.length - 1 ? (
                <>
                  <Check size={20} />
                  Terminer
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </div>

          {step === 0 && (
            <button
              onClick={handleSkipTutorial}
              className="w-full mt-4 text-sm text-slate-400 hover:text-slate-600 transition"
            >
              Passer le tutoriel
            </button>
          )}
        </div>
      </div>
    );
  }

  // Tooltip pointant vers un élément
  return (
    <>
      {/* Overlay sombre */}
      <div className="fixed inset-0 bg-black/40 z-[90] animate-in fade-in" />

      {/* Highlight de l'élément cible */}
      <div className="fixed z-[95]" style={{ pointerEvents: 'none' }}>
        {/* Le positionnement sera fait via JS dans une vraie implémentation */}
      </div>

      {/* Tooltip */}
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-[100] animate-in slide-in-from-bottom">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-bold text-slate-800">{currentStep.title}</h3>
            <button
              onClick={handleSkipTutorial}
              className="text-slate-400 hover:text-slate-600 transition"
            >
              <X size={20} />
            </button>
          </div>

          <p className="text-slate-600 text-sm mb-4">{currentStep.description}</p>

          <div className="flex items-center gap-2 mb-4">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  i <= step ? 'bg-blue-500' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-3">
            {step > 0 && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition text-sm flex items-center gap-1"
              >
                <ChevronLeft size={14} />
                Retour
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition text-sm"
            >
              {step === steps.length - 1 ? 'Terminer' : 'Suivant'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default InteractiveTutorial;
